/**
 * board-route — hash-based routing for the single-pane evaluator SPA (Wave 2c).
 *
 * The daemon's static handler (`src/daemon/ui-static.ts`) serves only `GET /`
 * (index.html) and `GET /assets/*` — every other real path 404s. So the SPA
 * routes in the URL HASH, not the path, and a hash link always loads
 * `index.html`, so a shared link survives a hard reload.
 *
 * The redesign collapses the former duplicate surfaces (Pipeline / Runs / History
 * all rendered the same pane) into a coherent two-view sidebar — the run-fidelity
 * rollup that used to be its own "Runs" tab is now reachable via the in-pane
 * fidelity rocker, so it is no longer a redundant nav entry:
 *
 *   #/         → Pipeline   (the live host board — all trials/runs; the default)
 *   #/history  → History    (the finished-run tail — terminal work only)
 *
 * The route set is intentionally extensible: a sibling effort adds a top-level
 * `host` view on top of this structure, so `Route` is a discriminated union and
 * the App derives its nav from a single view table — adding a view is one entry
 * here plus one parse branch.
 *
 * Orthogonally, `#trial=<id>` overlays the single-trial drawer on whichever view
 * is active (see trial-link.ts) — it is NOT a view route, so it parses here as the
 * default Pipeline view and the drawer hook reads it independently.
 *
 * This module is the pure parse/build layer — no `window`, no listeners — so the
 * route table is unit-testable. The App wires it to `location.hash`/`hashchange`.
 */

/** The Pipeline home view — the live host board (all trials/runs). */
export interface PipelineRoute {
  kind: 'pipeline'
}

/** The History view — the finished-run tail (terminal runs only). */
export interface HistoryRoute {
  kind: 'history'
}

/** The Host view — the box's load/health surface (CPU/load graph + full panel). */
export interface HostRoute {
  kind: 'host'
}

export type Route = PipelineRoute | HistoryRoute | HostRoute

const PIPELINE: PipelineRoute = { kind: 'pipeline' }
const HISTORY: HistoryRoute = { kind: 'history' }
const HOST: HostRoute = { kind: 'host' }

/**
 * Parse a location hash into a route. `#/history` is the finished tail; `#/host`
 * is the host load/health view; anything else — the empty hash, `#/`, the retired
 * `#/runs` deep link, the drawer's `#trial=<id>` overlay, or a stale link — falls
 * back to the Pipeline home.
 */
export function parseRoute(hash: string): Route {
  const normalized = hash.replace(/^#/, '')
  if (/^\/history\/?$/.test(normalized)) {
    return HISTORY
  }
  if (/^\/host\/?$/.test(normalized)) {
    return HOST
  }
  return PIPELINE
}

/** Build the hash for the Pipeline home view. */
export function pipelineHash(): string {
  return '#/'
}

/** Build the hash for the History (finished-run) view. */
export function historyHash(): string {
  return '#/history'
}

/** Build the hash for the Host (load/health) view. */
export function hostHash(): string {
  return '#/host'
}

/** Map a sidebar nav id to its view hash. */
export function hashForNav(navId: Route['kind']): string {
  switch (navId) {
    case 'history':
      return historyHash()
    case 'host':
      return hostHash()
    default:
      return pipelineHash()
  }
}
